import Phaser from 'phaser'
import { Creature } from '../entities/Creature'
import { EVENTS } from '../types/GameTypes'

export class TargetIndicator {
  private scene: Phaser.Scene
  private graphics: Phaser.GameObjects.Graphics
  private currentTarget: Creature | null = null

  // Animation
  private rotationAngle: number = 0
  private pulseScale: number = 1

  constructor(scene: Phaser.Scene) {
    this.scene = scene

    // Create graphics object for the indicator
    this.graphics = scene.add.graphics()
    this.graphics.setDepth(5) // Above ground, below creatures

    // Listen for target changes
    this.scene.events.on(EVENTS.TARGET_CHANGED, this.onTargetChanged, this)
  }

  private onTargetChanged(target: Creature | null): void {
    this.currentTarget = target
    if (!target) {
      this.graphics.clear()
    }
  }

  /**
   * Update the indicator position and animation
   */
  public update(delta: number): void {
    if (!this.currentTarget || !this.currentTarget.isAlive()) {
      this.graphics.clear()
      return
    }

    // Update animation
    this.rotationAngle += delta * 0.002
    this.pulseScale = 1 + Math.sin(this.rotationAngle * 3) * 0.1

    // Draw indicator
    this.drawIndicator()
  }

  private drawIndicator(): void {
    if (!this.currentTarget) return

    this.graphics.clear()

    const x = this.currentTarget.x
    const y = this.currentTarget.y
    const radius = 24 * this.pulseScale

    // Outer rotating dashed circle
    this.graphics.lineStyle(2, 0xef4444, 0.8)

    // Draw dashed circle manually
    const segments = 12
    const dashLength = (2 * Math.PI) / segments / 2

    for (let i = 0; i < segments; i++) {
      const startAngle = this.rotationAngle + (i * 2 * Math.PI) / segments
      const endAngle = startAngle + dashLength

      this.graphics.beginPath()
      this.graphics.arc(x, y, radius, startAngle, endAngle)
      this.graphics.strokePath()
    }

    // Inner solid circle (subtle)
    this.graphics.lineStyle(1, 0xef4444, 0.4)
    this.graphics.beginPath()
    this.graphics.arc(x, y, radius * 0.6, 0, Math.PI * 2)
    this.graphics.strokePath()

    // Small triangular pointers
    const pointerDist = radius + 8
    const pointerSize = 6

    this.graphics.fillStyle(0xef4444, 0.8)

    // Top pointer
    this.drawPointer(x, y - pointerDist, 0, pointerSize)
    // Bottom pointer
    this.drawPointer(x, y + pointerDist, Math.PI, pointerSize)
    // Left pointer
    this.drawPointer(x - pointerDist, y, -Math.PI / 2, pointerSize)
    // Right pointer
    this.drawPointer(x + pointerDist, y, Math.PI / 2, pointerSize)
  }

  private drawPointer(x: number, y: number, rotation: number, size: number): void {
    const cos = Math.cos(rotation)
    const sin = Math.sin(rotation)

    // Triangle points
    const p1x = x + cos * size
    const p1y = y + sin * size
    const p2x = x + Math.cos(rotation + 2.5) * size * 0.7
    const p2y = y + Math.sin(rotation + 2.5) * size * 0.7
    const p3x = x + Math.cos(rotation - 2.5) * size * 0.7
    const p3y = y + Math.sin(rotation - 2.5) * size * 0.7

    this.graphics.fillTriangle(p1x, p1y, p2x, p2y, p3x, p3y)
  }

  /**
   * Clean up
   */
  public destroy(): void {
    this.scene.events.off(EVENTS.TARGET_CHANGED, this.onTargetChanged, this)
    this.graphics.destroy()
  }
}
